import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import {
  disableVendorAsync,
  enableVendorAsync,
  fetchUserByIdAsync,
} from "../../../../redux/features/adminSlice";
import { RootState } from "../../../../redux/app/store";
import moment from "moment";
import {
  ArrowLeft,
  ShieldCheck,
  ShieldOff,
  Star,
  Globe,
  ExternalLink,
} from "lucide-react";

const VendorDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedUser, isLoading } = useAppSelector(
    (state: RootState) => state.admin,
  );
  const [showConfirm, setShowConfirm] = useState<"disable" | "enable" | null>(
    null,
  );
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);

  // Fetch full user profile on mount
  useEffect(() => {
    if (id) {
      setIsFetchingProfile(true);
      dispatch(fetchUserByIdAsync(id)).finally(() =>
        setIsFetchingProfile(false),
      );
    }
  }, [id, dispatch]);

  const vendor = selectedUser;

  // Skeleton placeholder component
  const SkeletonBlock = ({ className = "" }: { className?: string }) => (
    <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
  );

  if (isFetchingProfile) {
    return (
      <div className="flex flex-col gap-6 md:p-8 p-4 bg-gray-100 min-h-screen">
        <div className="flex items-center gap-4">
          <button
            className="text-gray-500 hover:text-gray-800 flex items-center font-bold bg-gray-50 p-2 rounded"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <SkeletonBlock className="h-7 w-44" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card Skeleton */}
          <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col items-center">
            <SkeletonBlock className="w-24 h-24 rounded-full mb-4" />
            <SkeletonBlock className="h-5 w-36 mb-2" />
            <SkeletonBlock className="h-4 w-28 mb-3" />
            <SkeletonBlock className="h-6 w-24 rounded-full mb-2" />
            <SkeletonBlock className="h-6 w-28 rounded-full mb-2" />
            <div className="mt-3 w-full flex flex-col gap-2">
              <SkeletonBlock className="h-10 w-full rounded-lg" />
              <SkeletonBlock className="h-10 w-full rounded-lg" />
            </div>
            <div className="mt-6 w-full">
              <SkeletonBlock className="h-10 w-full rounded-lg" />
            </div>
          </div>
          {/* Details Skeleton */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {[1, 2].map((section) => (
              <div key={section} className="bg-white rounded-2xl p-6 shadow-sm">
                <SkeletonBlock className="h-5 w-40 mb-4" />
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <div key={row} className="flex items-center py-3 border-b border-gray-100">
                    <SkeletonBlock className="h-4 w-32 mr-6" />
                    <SkeletonBlock className="h-4 w-48" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="flex flex-col gap-6 md:p-8 p-4 bg-gray-100 min-h-screen">
        <button
          className="text-gray-500 hover:text-gray-800 flex items-center font-bold max-w-24 space-x-2 mb-4 bg-gray-50 p-2 rounded"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500 text-lg">No vendor data found.</p>
        </div>
      </div>
    );
  }

  const handleDisable = async () => {
    try {
      await dispatch(disableVendorAsync(vendor.id)).unwrap();
      // Re-fetch profile to get updated business.status
      if (id) dispatch(fetchUserByIdAsync(id));
    } catch (error) {
      // Error toast handled in thunk
    } finally {
      setShowConfirm(null);
    }
  };

  const handleEnable = async () => {
    try {
      await dispatch(enableVendorAsync(vendor.id)).unwrap();
      // Re-fetch profile to get updated business.status
      if (id) dispatch(fetchUserByIdAsync(id));
    } catch (error) {
      // Error toast handled in thunk
    } finally {
      setShowConfirm(null);
    }
  };

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => (
    <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-100">
      <span className="text-sm text-gray-500 font-medium sm:w-48 min-w-[12rem]">
        {label}
      </span>
      <span className="text-sm text-gray-900 mt-1 sm:mt-0">
        {value || "N/A"}
      </span>
    </div>
  );

  const StatusBadge = ({ value, trueText = "Yes", falseText = "No" }: { value: boolean | null | undefined; trueText?: string; falseText?: string }) => (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
        value ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {value ? trueText : falseText}
    </span>
  );

  return (
    <div className="flex flex-col gap-6 md:p-8 p-4 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            className="text-gray-500 hover:text-gray-800 flex items-center font-bold bg-gray-50 p-2 rounded"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <h1 className="text-lg md:text-2xl font-bold">Vendor Details</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col items-center">
          {vendor?.profileImage ? (
            <img
              src={vendor.profileImage}
              alt={vendor?.fullName || "Vendor"}
              className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-purple-200"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center text-3xl font-bold text-purple-600 mb-4">
              {vendor?.fullName?.charAt(0)?.toUpperCase() ||
                vendor?.firstName?.charAt(0)?.toUpperCase() ||
                "V"}
            </div>
          )}
          <h2 className="text-lg font-semibold text-gray-900">
            {vendor?.fullName ||
              `${vendor?.firstName ?? ""} ${vendor?.lastName ?? ""}`.trim() ||
              "N/A"}
          </h2>
          <p className="text-sm text-gray-500">@{vendor?.username || "N/A"}</p>
          <span className="mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
            Status: {vendor?.status || "N/A"}
          </span>

          {/* Show actual user type badge */}
          {vendor?.userType && (
            <span className="mt-2 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Account type: {vendor?.userType}
            </span>
          )}

          {/* KYC Status badge */}
          {vendor?.kycStatus && (
            <span className="mt-2 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              KYC: {vendor.kycStatus}
            </span>
          )}

          {/* Ambassador badge */}
          {vendor?.isAmbassador && (
            <span className="mt-2 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              🏅 Ambassador
            </span>
          )}

          {/* Rating & Buyer Count */}
          <div className="mt-3 w-full flex flex-col gap-2">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-yellow-500" /> Avg. Rating
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {vendor?.averageRating != null ? vendor.averageRating : "N/A"}
              </span>
            </div>
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500">Buyer Count</span>
              <span className="text-sm font-semibold text-gray-900">
                {vendor?.buyerCount != null ? vendor.buyerCount : "N/A"}
              </span>
            </div>
          </div>

          {/* Disable / Enable Button — driven by business.status */}
          <div className="mt-6 w-full flex flex-col gap-2">
            {!vendor?.business ? (
              <p className="text-xs text-gray-400 text-center">
                Vendor has not set up business information yet.
              </p>
            ) : vendor.business.status?.toUpperCase() === "ACTIVE" ? (
              <button
                onClick={() => setShowConfirm("disable")}
                disabled={isLoading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-red-600 hover:bg-red-700 text-white ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <ShieldOff className="w-4 h-4" /> Disable Vendor
              </button>
            ) : (
              <button
                onClick={() => setShowConfirm("enable")}
                disabled={isLoading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-green-600 hover:bg-green-700 text-white ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <ShieldCheck className="w-4 h-4" /> Enable Vendor
              </button>
            )}
          </div>
        </div>

        {/* Details Cards */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Personal Information */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Personal Information
            </h3>
            <InfoRow
              label="Full Name"
              value={
                vendor?.fullName ||
                `${vendor?.firstName ?? ""} ${vendor?.lastName ?? ""}`.trim()
              }
            />
            <InfoRow label="Username" value={vendor?.username} />
            <InfoRow label="Email" value={vendor?.email} />
            <InfoRow label="Other Name" value={vendor?.othername} />
            <InfoRow label="Gender" value={vendor?.gender} />
            <InfoRow label="Phone Number" value={vendor?.phoneNumber} />
            <InfoRow
              label="Date of Birth"
              value={
                vendor?.dob
                  ? moment(vendor.dob).format("MMMM DD, YYYY")
                  : null
              }
            />
            <InfoRow
              label="City"
              value={
                typeof vendor?.city === "string"
                  ? vendor?.city
                  : vendor?.city?.name
              }
            />
            <InfoRow label="State" value={vendor?.state} />
            <InfoRow label="Address" value={vendor?.address} />
            <InfoRow label="Shipping Address" value={vendor?.shippingAddress} />
            <InfoRow label="Language" value={vendor?.language} />
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Account Information
            </h3>
            <InfoRow label="User Type" value={vendor?.userType} />
            <InfoRow label="Roles" value={vendor?.roles} />
            <InfoRow label="KYC Status" value={vendor?.kycStatus} />
            <InfoRow label="Referrer Code" value={vendor?.referrerCode} />
            <InfoRow
              label="Ambassador"
              value={<StatusBadge value={vendor?.isAmbassador} />}
            />
            <InfoRow
              label="Email Verified"
              value={<StatusBadge value={vendor?.emailVerified} />}
            />
            <InfoRow
              label="Phone Verified"
              value={<StatusBadge value={vendor?.phoneVerified} />}
            />
            <InfoRow
              label="2FA Enabled"
              value={<StatusBadge value={vendor?.twoFaEnabled} />}
            />
            <InfoRow
              label="Last Login"
              value={
                vendor?.lastLogin
                  ? moment(vendor.lastLogin).format("MMMM DD, YYYY [at] h:mm A")
                  : null
              }
            />
            <InfoRow
              label="Registered"
              value={
                vendor?.createdAt
                  ? moment(vendor.createdAt).format("MMMM DD, YYYY [at] h:mm A")
                  : null
              }
            />
            <InfoRow
              label="Last Updated"
              value={
                vendor?.updatedAt
                  ? moment(vendor.updatedAt).format("MMMM DD, YYYY [at] h:mm A")
                  : null
              }
            />
            {vendor?.deactivationReason && (
              <InfoRow label="Deactivation Reason" value={vendor.deactivationReason} />
            )}
            {vendor?.deactivationDate && (
              <InfoRow
                label="Deactivation Date"
                value={moment(vendor.deactivationDate).format("MMMM DD, YYYY [at] h:mm A")}
              />
            )}
            {vendor?.reactivationDate && (
              <InfoRow
                label="Reactivation Date"
                value={moment(vendor.reactivationDate).format("MMMM DD, YYYY [at] h:mm A")}
              />
            )}
          </div>

          {/* Wallet */}
          {vendor?.wallet && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Wallet
              </h3>
              <InfoRow label="Wallet Status" value={vendor.wallet.status} />
              <InfoRow label="Withdrawable Balance" value={vendor.wallet.withdrawableBalance != null ? `₦${Number(vendor.wallet.withdrawableBalance).toLocaleString()}` : null} />
              <InfoRow label="Tied Balance" value={vendor.wallet.tiedBalance != null ? `₦${Number(vendor.wallet.tiedBalance).toLocaleString()}` : null} />
              <InfoRow label="Pending Withdrawal" value={vendor.wallet.pendingWithdrawalBalance != null ? `₦${Number(vendor.wallet.pendingWithdrawalBalance).toLocaleString()}` : null} />
              <InfoRow label="Total Withdrawal" value={vendor.wallet.totalWithdrawal != null ? `₦${Number(vendor.wallet.totalWithdrawal).toLocaleString()}` : null} />
              <InfoRow label="Total Deposit" value={vendor.wallet.totalDeposit != null ? `₦${Number(vendor.wallet.totalDeposit).toLocaleString()}` : null} />
            </div>
          )}

          {/* KYC Record */}
          {vendor?.kycRecord && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                KYC Record
              </h3>
              <InfoRow
                label="Phone Verified"
                value={<StatusBadge value={vendor.kycRecord.phoneVerified} />}
              />
              <InfoRow
                label="Address Verified"
                value={<StatusBadge value={vendor.kycRecord.addressVerified} />}
              />
              <InfoRow
                label="BVN Verified"
                value={<StatusBadge value={vendor.kycRecord.bvnVerified} />}
              />
              <InfoRow
                label="NIN Verified"
                value={<StatusBadge value={vendor.kycRecord.ninVerified} />}
              />
              <InfoRow
                label="CAC Verified"
                value={<StatusBadge value={vendor.kycRecord.cacVerified} />}
              />
              <InfoRow label="Daily Spent" value={vendor.kycRecord.dailySpent != null ? `₦${Number(vendor.kycRecord.dailySpent).toLocaleString()}` : null} />
              <InfoRow label="Monthly Spent" value={vendor.kycRecord.monthlySpent != null ? `₦${Number(vendor.kycRecord.monthlySpent).toLocaleString()}` : null} />
            </div>
          )}

          {/* Bank Accounts */}
          {vendor?.bankAccounts && vendor.bankAccounts.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Bank Accounts
              </h3>
              {vendor.bankAccounts.map((bank: any, index: number) => (
                <div key={index} className={index > 0 ? "mt-4 pt-4 border-t border-gray-200" : ""}>
                  <InfoRow label="Account Name" value={bank?.accountName} />
                  <InfoRow label="Account Number" value={bank?.accountNumber} />
                  <InfoRow label="Bank Name" value={bank?.bankName} />
                  <InfoRow
                    label="Primary"
                    value={<StatusBadge value={bank?.isPrimary} />}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Business Information */}
          {vendor?.business && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Business Information
              </h3>
              <InfoRow label="Business Name" value={vendor?.business?.name} />
              <InfoRow
                label="Business Email"
                value={vendor?.business?.businessEmail}
              />
              <InfoRow
                label="Business Phone"
                value={vendor?.business?.businessPhoneNumber}
              />
              <InfoRow
                label="Business City"
                value={vendor?.business?.businessCity}
              />
              {vendor?.business?.businessLogo && (
                <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500 font-medium sm:w-48 min-w-[12rem]">
                    Business Logo
                  </span>
                  <img
                    src={vendor.business.businessLogo}
                    alt="Business Logo"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                </div>
              )}
              <InfoRow
                label="Websites"
                value={
                  vendor?.business?.websites &&
                  (Array.isArray(vendor.business.websites)
                    ? vendor.business.websites.length > 0
                    : vendor.business.websites) ? (
                    <div className="flex flex-col gap-1">
                      {(Array.isArray(vendor.business.websites)
                        ? vendor.business.websites
                        : [vendor.business.websites]
                      ).map((url: string, i: number) => (
                        <a
                          key={i}
                          href={url.startsWith("http") ? url : `https://${url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:text-purple-800 underline flex items-center gap-1 text-sm"
                        >
                          <Globe className="w-3.5 h-3.5" /> {url}
                        </a>
                      ))}
                    </div>
                  ) : (
                    "N/A"
                  )
                }
              />
            </div>
          )}

          {/* Socials */}
          {vendor?.socials && vendor.socials.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Social Accounts
              </h3>
              {vendor.socials.map((social: any, index: number) => (
                <InfoRow
                  key={index}
                  label={social?.name || `Social ${index + 1}`}
                  value={
                    social?.url ? (
                      <a
                        href={
                          social.url.startsWith("http")
                            ? social.url
                            : `https://${social.url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> {social.url}
                      </a>
                    ) : (
                      "N/A"
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {showConfirm === "enable" ? "Enable Vendor" : "Disable Vendor"}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {showConfirm === "enable"
                ? `Are you sure you want to enable "${vendor?.fullName || "this vendor"}"? Their previously approved products will become visible in discovery again.`
                : `Are you sure you want to disable "${vendor?.fullName || "this vendor"}"? All their approved products will be automatically disapproved from discovery.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(null)}
                disabled={isLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={
                  showConfirm === "enable" ? handleEnable : handleDisable
                }
                disabled={isLoading}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors ${
                  showConfirm === "enable"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isLoading
                  ? "Processing..."
                  : showConfirm === "enable"
                    ? "Yes, Enable"
                    : "Yes, Disable"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDetails;
